/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { BrButton } from '@govbr-ds/react-components';
import { IconButton, Tooltip } from '@material-ui/core';

import { GovBrAccordion, GovBrTable } from 'components';
import { connectorRequests } from 'apiServices/connectorMonitorAPI/connectorMonitor';

import { useNewConsentModalContext } from 'pages/SharePage/Context/NewConsentModalContext';
import { Info, Textsms } from '@material-ui/icons';
import { DropDownFilter } from './DropDownFilter';

import '../IntegratedDocumentsPage.css';
import { LogErrorDialog } from './LogErrorDialog';
import { elasticRequests } from 'apiServices/elasticSearchAPI/elasticRequests';
import { toast } from 'react-toastify';
import { AGHUInfoDialog } from './AGHUInfoDialog';

const dictTypeDocuments = {
  PatientData: 'Cadastro de paciente',
  RACData: 'Registro de atendimento clínico',
  SAData: 'Sumário de alta',
  AttachmentModel: 'Documento assinado (atestados, prescrições, etc.)',
  ResultadoExameData: 'Resultado de exame',
};

function cleanConnectorResponse(data) {
  const responseItem = data
    ?.map((el) => {
      const populateActions = el?.action
        ? el.action?.map((act) => {
            const value = {
              patientId: act?.parameters?.patient || act?.parameters?.patientId,
              documentId:
                act?.parameters?.identifier || act?.parameters?.localId,
              documentType: dictTypeDocuments[act?.parameters?.model],
              organization: el.organization,
              status: el.status,
              startDate: el?.created || '-',
              endDate: el?.updated || '-',
            };
            return value;
          })
        : {
            patientId: '-',
            documentId: '-',
            documentType: '-',
            organization: el.organization,
            status: el.status,
            startDate: el?.created || '-',
            endDate: el?.updated || '-',
          };
      return populateActions;
    })
    ?.filter((e) => e !== undefined);

  const lines = [].concat([], ...responseItem);
  return lines;
}

export function ErrorAccordion({
  data,
  elasticFilter,
  connectorFilter,
  setData,
  setConnectorResponse,
}) {
  const { organizationList } = useNewConsentModalContext();
  const [filter, setFilter] = useState({});
  const [selected, setSelected] = useState({});
  const [messageError, setMessageError] = useState('');
  const isFilterApply = filter && Object.values(filter).includes(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [aghuOpen, setAghuOpen] = useState(false);
  const [aghuInfo, setAghuInfo] = useState({});
  const [errorData, setErrorData] = useState({});
  const [successCount, setSucessCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const refetch = async () => {
    try {
      const patient_link = await elasticRequests.PatientLink({
        filter: elasticFilter,
      });

      const local_Id = patient_link.hits.hits
        .map((el) => `local_id:"${el?._source?.local_id}"`)
        ?.join(' OR ');

      const connector_audit = await elasticRequests.ConnectorAudit({
        filter: local_Id,
      });

      const merge = patient_link.hits.hits.map((el) => {
        const reference = connector_audit.hits.hits.find(
          (e) =>
            e?._source?.instance === el?._source?.instance &&
            e?._source?.model === el?._source?.model &&
            e?._source?.local_id === el?._source?.local_id,
        );
        return {
          ...el._source,
          status: reference?._source?.status,
        };
      });

      setData(merge.filter((el) => el.status));

      const conector_monitor = await connectorRequests.getTasks(
        connectorFilter,
      );

      setConnectorResponse(
        cleanConnectorResponse(conector_monitor?.['_items'] || []),
      );
    } catch (err) {
      toast.error(
        'Não foi possível finalizar a operação, verifique os filtros e tente novamente',
      );
    } finally {
    }
  };

  const handleModalOpen = (e) => {
    setMessageError(e.status);
    setErrorData({
      local_id: e.local_id,
      cnes: e.cnes,
      model: e.model,
      status: e.status,
    });
    setModalOpen(true);
  };

  const handleAghuOpen = (e) => {
    setAghuOpen(true);
    setAghuInfo(e);
  };

  const dictTypeStatus = {
    SUCCESS: 'Sucesso',
    NETWORK_ERROR: 'ERRO 000 - Falha de conexão',
    PATIENT_DEMOGRAPHICS_ERROR:
      'ERRO 409 - Dados do paciente não conferem com o Cadastro Nacional de Saúde',
    PATIENT_IDENTIFIER_ERROR:
      'ERRO 404 - Identificação do paciente ausente ou inconsistente',
    SERVER_ERROR: 'ERRO 500 - Erro desconhecido no processamento',
    VALIDATION_ERROR: 'ERRO 000 - Informação inválida no atendimento',
  };

  const rows = data?.map((e) => ({
    ...e,
    '@timestamp': formatDateTime(e['@timestamp']),
    displayModel: dictTypeDocuments[e?.model] || e?.model || e.tags,
    statusValue: dictTypeStatus[e.status],
    status: (
      <div
        style={{
          width: 'fit-content',

          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {dictTypeStatus[e.status]}
        {e.model === 'ResultadoExameData' && (
          <Tooltip title="Integrações com AGHU">
            <IconButton
              onClick={() =>
                handleAghuOpen({ localId: e?.local_id, cnes: e?.cnes })
              }
            >
              <Info />
            </IconButton>
          </Tooltip>
        )}
      </div>
    ),
    disabled: e.activeSolicitating,
    reprocessar: (
      <IconButton className="smsIcon" onClick={() => handleModalOpen(e)}>
        <Textsms />
      </IconButton>
    ),
    organization_name: organizationList?.find(
      (el) => el?.resource?.identifier?.[0]?.value === e?.cnes,
    )?.resource?.alias,
  }));

  const options = rows?.map((el) => ({ display: el?.statusValue }));

  const displayRows = isFilterApply
    ? rows?.filter((element) => filter?.[element?.status])
    : rows;

  const selectedRows = displayRows?.filter((el) => selected[el?.local_id]);

  const header = [
    { display: 'CPF ou CNS', path: 'patient_id' },
    { display: 'ID do documento', path: 'local_id' },
    { display: 'Tipo do documento', path: 'displayModel' },
    { display: 'Data do processamento', path: '@timestamp' },
    { display: 'Unidade hospitalar', path: 'organization_name' },
    {
      display: (
        <DropDownFilter
          title="Resultado do processamento"
          filter={filter}
          setFilter={setFilter}
          options={options}
        />
      ),
      path: 'status',
    },
    {
      display: (
        <BrButton
          primary
          onClick={async () => {
            try {
              await selectedRows?.map(async (el) => {
                const postTask = await connectorRequests.postTasks({
                  cnes: el?.cnes,
                  instance: el?.instance.split('_')?.[1]?.replace(/\d$/, '1'),
                  identifier: el?.local_id,
                  model: el?.model,
                  patient: el?.patient_id,
                });
                if (postTask === 'internal_error') {
                  toast.error(
                    `Erro interno! Não foi possível reprocessar o documento ${el?.local_id}. `,
                  );
                  return;
                }
                if (selectedRows?.length === 1)
                  toast.success(
                    `Solicitação de reprocessamento realizada com sucesso para o documento ${el?.local_id}`,
                  );
                setTotalCount(selectedRows?.length);
                setSucessCount((prev) => prev + 1);
              });
            } catch (err) {
              console.log('err', err);
            } finally {
              setSelected({});
              await refetch();
            }
          }}
        >
          Reprocessar
        </BrButton>
      ),
      path: 'reprocessar',
    },
  ];

  const accordion = {
    title: 'Erro de processamento',
    content: (
      <GovBrTable
        header={header}
        body={displayRows}
        selected={selected}
        setSelected={setSelected}
        checkbox
      />
    ),
  };

  useEffect(() => {
    if (successCount > 1) {
      toast.success(
        `Solicitação de reprocessamento realizada com sucesso para ${successCount} de ${totalCount} documentos.`,
      );
      setSucessCount(0);
      setTotalCount(0);
    }
  }, [successCount]);

  // Função para formatar data no padrão DD/MM/YYYY - HH:mm
  function formatDateTime(dateTime) {
    if (!dateTime) return '-';

    // Criar uma data a partir do timestamp
    const date = new Date(dateTime);

    if (Number.isNaN(date.getTime())) return '-';

    // Ajustar para o fuso horário correto (adicionar 3 horas para corrigir o problema)
    const adjustedDate = new Date(date.getTime() + 3 * 60 * 60 * 1000);

    return adjustedDate
      .toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      .replace(',', ' - ');
  }

  return (
    <>
      <GovBrAccordion
        name={accordion?.title}
        content={accordion?.content}
        total={displayRows?.length}
        showMoreIndicator={data.length >= 1000}
      />

      <LogErrorDialog
        open={modalOpen}
        setModalOpen={setModalOpen}
        messageError={messageError}
        errorData={errorData}
      />

      {aghuOpen && (
        <AGHUInfoDialog
          open={aghuOpen}
          setModalOpen={setAghuOpen}
          data={aghuInfo}
        />
      )}
    </>
  );
}
